import { trigger } from '@angular/animations';
import { isPlatformServer, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, PLATFORM_ID, ViewChild } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { fadeTopAndBottomIf } from 'src/app/animations/fade-top-and-bottom.if.animation';
import { fadeUpIf } from 'src/app/animations/fade-up.if.animation';
import { fadeIf } from 'src/app/animations/fade.if.animation';
import { Rect } from 'src/app/classes/rect.class';
import { ScreenSizes } from 'src/app/classes/screen-sizes.class';
import { getEventPath } from 'src/app/lib/get-event-path';
import { getScrollbarWidth } from 'src/app/lib/get-scrollbar-width';
import { sleep } from 'src/app/lib/sleep';
import { disableBodyScroll } from 'src/app/lib/toggle-body-scroll';
import { BrowserService } from 'src/app/services/browser.service';
import { ContextMenuService } from 'src/app/services/context-menu.service';
import { ResizeDirective } from '../../shared/directives/resize.directive';

export type ContextMenuOptions = {
  noSpacing?: boolean;
  doubleSpacing?: boolean;
  width?: string;
  background?: 'light' | 'medium' | 'strong' | 'none' | 'mobile';
  closeOnBgClick?: boolean;
  openOnHover?: boolean;
  hoverDelay?: number;
  bigBorderRadius?: boolean;
  allowClicks?: boolean;
};

@Component({
  selector: 'app-context-menu-global',
  templateUrl: './context-menu-global.component.html',
  styleUrls: ['./context-menu-global.component.scss'],
  animations: [
    trigger('contextBackground', fadeIf),
    trigger('contextDialog', fadeTopAndBottomIf),
    trigger('contextDialogMobile', fadeUpIf),
  ],
  standalone: true,
  imports: [ResizeDirective, NgIf, NgTemplateOutlet],
})
export class ContextMenuGlobalComponent {
  template: any;
  position: string;
  verticalPosition: string;
  private positionOrig: string;
  private verticalPositionOrig: string;
  clientEl: HTMLElement;
  component: any;
  options: ContextMenuOptions;
  pos = new Rect();
  defaultOptions: ContextMenuOptions = {
    noSpacing: false,
    width: 'auto',
    background: 'light',
    closeOnBgClick: false,
    bigBorderRadius: false,
  };
  listenForClicks = false;
  interactive = false;
  clientWidth: number;
  maxHeight: number;
  @ViewChild('contextMenuContainer') contextMenuContainer: ElementRef;
  @HostListener('window:resize', ['$event']) winResize() {
    this.calcPositions();
    this.maxHeight = window.innerHeight - 72;
  }
  @HostListener('window:scroll', ['$event']) winScroll() {
    this.calcPositions();
  }
  @HostListener('document:click', ['$event'])
  async clickout(event: any) {
    if (!this.listenForClicks) return;
    const path = getEventPath(event);
    if (path.includes(this.eRef.nativeElement)) return;
    await sleep(0);
    this.close();
  }
  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.target['form']) return;

    if (event.key === 'Escape') {
      this.close();
    }
  }
  @HostListener('mouseenter') enter() {
    this.contextMenuService.hoveringOverContextContent = true;
    this.contextMenuService._contextContentHover.next(true);
  }
  @HostListener('mouseleave') leave() {
    this.contextMenuService.hoveringOverContextContent = false;
    this.contextMenuService._contextContentHover.next(false);
  }

  constructor(
    private contextMenuService: ContextMenuService,
    private router: Router,
    private eRef: ElementRef,
    private browserService: BrowserService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformServer(this.platformId)) return;
    this.maxHeight = window.innerHeight - 72;
    this.contextMenuService.onContextOpen.subscribe(async data => {
      this.listenForClicks = false;
      if (data) {
        await sleep(0);
        this.interactive = false;
        this.template = data.template;
        this.component = data.component;
        this.position = this.positionOrig = data.position;
        this.verticalPosition = this.verticalPositionOrig = data.verticalPosition;
        this.clientEl = data.clientEl;
        this.options = { ...this.defaultOptions, ...(data.options || {}) };
        if (ScreenSizes.screenSize() === 'mobile') {
          this.options.background = 'mobile'; // mobile should always have background
          this.options.closeOnBgClick = true;
          disableBodyScroll(undefined, this.browserService.isExtension());
        }
        let box = this.clientEl.getBoundingClientRect();
        this.clientWidth = box.width;
        this.calcPositions();
        await sleep(0);
        if (!this.options.openOnHover) this.listenForClicks = true;
        await sleep(200);
        this.interactive = true;
      } else {
        this.template = undefined;
        this.contextMenuService.hoveringOverContextContent = false;
      }
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // if (this.options.dontCloseOnNavigation) return;
        this.close();
      }
    });
  }

  close() {
    this.contextMenuService.hoveringOverContextContent = false;
    this.component?.close();
  }

  private getPosX() {
    if (!this.clientEl) return 0;
    const clientBox = this.clientEl.getBoundingClientRect();
    if (this.position === 'left') return clientBox.left;
    if (this.position === 'left-out') return clientBox.left + clientBox.width;
    if (this.position === 'center') return clientBox.left + this.clientWidth / 2;
    if (this.position === 'screen-center') return (window.innerWidth - getScrollbarWidth()) / 2;
    if (this.position === 'right')
      return Math.round(window.innerWidth - getScrollbarWidth() - (clientBox.left + this.clientWidth));
  }

  private getPosY() {
    if (!this.clientEl) return 0;
    const verticalPos = this.verticalPosition;
    const clientBox = this.clientEl.getBoundingClientRect();
    if (verticalPos === 'bottom') {
      return Math.round(clientBox.top + clientBox.height + document.scrollingElement.scrollTop);
    }
    if (verticalPos === 'bottom-in') {
      return Math.round(clientBox.top + document.scrollingElement.scrollTop);
    }
    if (verticalPos === 'top') {
      return Math.round(window.innerHeight - (clientBox.top + document.scrollingElement.scrollTop));
    }
    if (verticalPos === 'top-in') {
      return Math.round(window.innerHeight - (clientBox.top + clientBox.height + document.scrollingElement.scrollTop));
    }

    const contentBox = this.contextMenuContainer?.nativeElement?.getBoundingClientRect();
    if (!contentBox) return 0;
    let y = Math.round(clientBox.top + clientBox.height / 2 - contentBox.height / 2);
    return y + document.scrollingElement.scrollTop;
  }

  getPosition() {
    if (this.positionOrig !== 'auto') return this.positionOrig;
    const box = this.clientEl.getBoundingClientRect();
    const container = this.contextMenuContainer?.nativeElement.getBoundingClientRect();
    if (!container) return 'right';
    return box.left + container.width > window.innerWidth ? 'right' : 'left';
  }

  getVerticalPosition() {
    if (this.verticalPositionOrig !== 'auto') return this.verticalPositionOrig;
    const box = this.clientEl.getBoundingClientRect();
    const container = this.contextMenuContainer?.nativeElement.getBoundingClientRect();
    if (!container) return 'bottom';
    return box.top + box.height + container.height > window.innerHeight ? 'top' : 'bottom';
  }

  async calcPositions() {
    if (!this.template) return;
    // Doing this twice to avoid the scrollbar flashing in
    this.verticalPosition = this.getVerticalPosition();
    this.position = this.getPosition();
    this.pos.x = this.getPosX();
    this.pos.y = this.getPosY();
    await sleep(0);
    this.verticalPosition = this.getVerticalPosition();
    this.position = this.getPosition();
    this.pos.x = this.getPosX();
    this.pos.y = this.getPosY();
  }
}
